import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import type { AppointmentType, Provider } from 'app/generated/graphql'
import {
  AvailabilityRecord,
  readableProviderName,
  NameLookups,
  practiceToday,
  requestDaysForWeek,
  setupBlocksForDate,
  weekAnchor,
  weekDates,
  weekLabel,
} from 'app/lib/aiAgentAvailability'
import { DayBoard, buildDayBoard } from 'app/lib/aiAgentAvailabilityBoard'
import type { SchedulingData } from 'app/stores/AIAgentStore'
import {
  useAIAgentStore,
  useAppointmentTypeStore,
  useEntityList,
  useLocationStore,
  useProviderStore,
} from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { AI_AGENT_SETTINGS_OPERATORIES_QUERY } from './graphql'

export const MAX_WEEK_OFFSET = 7

/** One day of the week picker: whether anything is set up on it. */
export type WeekDay = { date: string; setUp: boolean; today: boolean }

export type AiAgentAvailability = {
  weekOffset: number
  setWeekOffset: (offset: number) => void
  weekLabel: string
  week: WeekDay[]
  selectedDate: string
  setSelectedDate: (date: string) => void
  /** The chosen day as a room board. */
  board: DayBoard
  loading: boolean
  failed: boolean
  nothingConfigured: boolean
  /** The availability records behind the board, for views that summarize them another way. */
  records: AvailabilityRecord[]
  names: NameLookups
  appointmentTypes: AppointmentType[]
  /** Providers the schedule lists as active; records for anyone else open nothing for the agent. */
  activeProviderIds: Set<string>
  /** Today on the practice's calendar. */
  today: string
}

const PROVIDER_LIST_PARAMS = { pagination: { limit: 500 } }

/**
 * The Availability section's data for one location and one week. Everything
 * comes from the `scheduling-data` read the patient scheduler already uses,
 * one request per month shown and cached in the store. The board
 * draws the availability records: who works in which room, when, and for what.
 */
export function useAiAgentAvailability(locationId: string | undefined): AiAgentAvailability {
  const agentStore = useAIAgentStore()
  const locationStore = useLocationStore()
  const timezone = locationId ? locationStore.lookup(locationId)?.timezone : undefined
  const today = practiceToday(timezone)
  const [weekOffset, setWeekOffset] = useState(0)
  const [selectedDate, setSelectedDate] = useState(today)
  const [months, setMonths] = useState<Record<string, SchedulingData | `failed`>>({})
  const appointmentTypeStore = useAppointmentTypeStore()
  const providerStore = useProviderStore()
  const { list: appointmentTypes } = useEntityList(appointmentTypeStore, {
    params: { locationId },
    skip: !locationId,
  })
  // The scheduling response lists active providers only, but a deactivated
  // provider can still hold availability records; the store names them all.
  const { list: providers } = useEntityList(providerStore, {
    params: PROVIDER_LIST_PARAMS,
    skip: !locationId,
  })

  const dates = useMemo(() => weekDates(weekAnchor(weekOffset)), [weekOffset])
  const requestDays = useMemo(() => requestDaysForWeek(dates), [dates])

  useEffect(() => {
    if (locationId) bg(locationStore.fetchOne(locationId))
  }, [locationId, locationStore])

  // A new practice or week starts on today when it is in view, else the first day.
  useEffect(() => {
    setSelectedDate(dates.includes(today) ? today : dates[0])
  }, [dates, today, locationId])

  useEffect(() => {
    setWeekOffset(0)
  }, [locationId])

  // One request per month shown, shared through the store so a week that is revisited never refetches.
  useEffect(() => {
    if (!locationId) return
    for (const day of requestDays) {
      const key = `${locationId}|${day}`
      const storeMonth = (month: SchedulingData | `failed`) =>
        setMonths((prev) => (prev[key] === month ? prev : { ...prev, [key]: month }))
      const failMonth = (err: Error) => {
        if (err.name === `AbortError`) return
        logError(`AI Agent Settings: scheduling-data failed for ${locationId} ${day}`, err)
        storeMonth(`failed`)
      }
      agentStore.schedulingMonth(locationId, day).then(storeMonth).catch(failMonth)
    }
  }, [agentStore, locationId, requestDays])

  useEffect(() => () => agentStore.abortScheduling(), [agentStore, locationId])

  // A PMS that is offline answers with an error; the grid then falls back to room numbers.
  const quietContext = useMemo(() => ({ suppressErrorToast: true }), [])
  const [operatoriesResult] = useQuery({
    query: AI_AGENT_SETTINGS_OPERATORIES_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId,
    context: quietContext,
  })

  const responses = requestDays.map((day) => months[`${locationId}|${day}`])
  const loading = responses.some((r) => r === undefined)
  const failed = responses.some((r) => r === `failed`)
  const loaded = useMemo(
    () => responses.filter((r): r is SchedulingData => !!r && r !== `failed`),
    // `responses` is rebuilt each render; its contents only change with these.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [months, requestDays, locationId],
  )

  const names = useMemo<NameLookups>(() => {
    const providerNames = new Map<string, string>()
    for (const p of providers) {
      const name = readableProviderName(p.pmsDisplayName) ?? (p.displayName || `Provider`)
      providerNames.set(p.id, p.deactivated ? `${name} (inactive)` : name)
    }
    for (const r of loaded) {
      for (const p of r.providers ?? [])
        if (!providerNames.has(p.id)) providerNames.set(p.id, p.displayName || `Provider`)
    }
    const operatories = new Map<string, string>()
    for (const op of operatoriesResult.data?.operatories ?? []) {
      if (op.id) operatories.set(String(op.id), op.display_name || op.name || `Op ${op.id}`)
      if (op.remote_id) operatories.set(String(op.remote_id), op.display_name || op.name || `Op ${op.remote_id}`)
    }
    const types = new Map<string, string>()
    for (const t of appointmentTypes) {
      if (t.vendorId) types.set(t.vendorId, t.alias || t.displayName || t.name || t.vendorId)
    }
    return {
      providerName: (id) => (id ? (providerNames.get(id) ?? `Provider`) : `Provider`),
      operatoryName: (id) => (id ? (operatories.get(id) ?? `Op ${id}`) : `Any room`),
      serviceName: (vendorId) => types.get(vendorId),
    }
  }, [loaded, operatoriesResult.data, appointmentTypes, providers])

  const records = useMemo(() => {
    const byId = new Map<string, AvailabilityRecord>()
    for (const r of loaded) {
      for (const a of r.availabilities ?? []) byId.set(`${a.id}|${a.operatoryId}|${a.specificDate ?? ``}`, a)
    }
    return [...byId.values()]
  }, [loaded])

  const activeProviderIds = useMemo(
    () => new Set(loaded.flatMap((response) => (response.providers ?? []).map((provider) => provider.id))),
    [loaded],
  )

  const rooms = useMemo(() => {
    const ids = new Set<string>()
    for (const r of records) if (r.operatoryId) ids.add(r.operatoryId)
    return [...ids]
      .map((id) => ({ id, label: names.operatoryName(id) }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
  }, [records, names])

  const week = useMemo<WeekDay[]>(
    () =>
      dates.map((date) => ({
        date,
        setUp: setupBlocksForDate(records, date, names).length > 0,
        today: date === today,
      })),
    [dates, records, names, today],
  )

  const board = useMemo(
    () => buildDayBoard(setupBlocksForDate(records, selectedDate, names), rooms),
    [records, selectedDate, names, rooms],
  )

  return {
    weekOffset,
    setWeekOffset,
    weekLabel: weekLabel(dates),
    week,
    selectedDate,
    setSelectedDate,
    board,
    loading,
    failed,
    nothingConfigured: !loading && !failed && records.length === 0,
    records,
    names,
    appointmentTypes,
    activeProviderIds,
    today,
  }
}
